import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { compose } from 'recompose';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import { Row, Column } from 'mage-components';
import { components as clientComponents } from 'mage-client';

import {
  Dropdown,
  Frame,
  IconCheckmark,
  IconClose,
  Link,
  List,
  Loader,
  Typography,
} from '@klarna/bubble-ui';

import { CAPS } from '@/constants';
import { getSupportedPlatforms } from '@/selectors/platforms';
import { getMerchantAwareTranslator } from '@/selectors/merchant';

import ExpandableFrame from './ExpandableFrame';
import { KlarnaLogo } from '@/components/ProductCard/KlarnaLogo';

const TitleWithKlarnaLogoContainer = styled.div`
  display: flex;
  align-items: center;
`;

const componentPropTypes = {
  currentConfig: PropTypes.shape({
    PRICING_ANNEX: PropTypes.object.isRequired,
  }).isRequired,
  selectedCountry: PropTypes.string.isRequired,
};

const enhancedComponentPropTypes = {
  getProducts: PropTypes.func.isRequired,
  supportedPlatforms: PropTypes.array.isRequired,
  t: PropTypes.func.isRequired,
};

type ComponentProps = PropTypes.InferProps<typeof componentPropTypes>;
type EnhancedComponentProps = ComponentProps &
  PropTypes.InferProps<typeof enhancedComponentPropTypes>;

export const ProductsComponent: React.FC<EnhancedComponentProps> = ({
  selectedCountry,
  currentConfig,
  getProducts,
  supportedPlatforms,
  t,
}) => {
  const [selectedPlatform, setSelectedPlatform] = useState<string>('shopify');
  const [products, setProducts] = useState<
    | {
        id: string;
        isCompatible: boolean;
        isCountryCompatible: boolean;
        meerkatsTemplateId: string;
        termsAndConditions: {
          href: string;
          country: string;
        }[];
      }[]
    | null
  >(null);

  useEffect(() => {
    setSelectedPlatform('shopify');
    setProducts(null);
  }, [selectedCountry]);

  useEffect(() => {
    setProducts(null);
    // eslint-disable-next-line promise/catch-or-return
    getProducts({
      params: { country: selectedCountry, platform: selectedPlatform },
    }).then((r: { data: any[] }) => setProducts(r.data.filter(({ id }) => id.includes('_one'))));
  }, [selectedCountry, selectedPlatform]);

  return (
    <>
      <Frame>
        <Row>
          <Column columns={{ mobile: 8 }}>
            <Typography textToken="text-style/headings/titles/bold/tertiary">Products</Typography>
          </Column>
          <Column columns={{ mobile: 4 }}>
            <Dropdown
              label="Platform"
              onChange={(v: string) => setSelectedPlatform(v)}
              value={selectedPlatform}
              options={[
                { key: '', label: '', disabled: true, hidden: true },
                ...supportedPlatforms.filter(({ supportedCountries }) =>
                  supportedCountries.includes(selectedCountry),
                ),
              ]}
              defaultValue="shopify"
            />
          </Column>
        </Row>
      </Frame>
      {products ? (
        products.map(p => (
          <ExpandableFrame
            key={p.id}
            icon={
              p.isCompatible && p.isCountryCompatible ? (
                <IconCheckmark colorToken="colors/text/success" />
              ) : (
                <IconClose colorToken="colors/text/error" />
              )
            }
            title={
              <>
                <TitleWithKlarnaLogoContainer>
                  <KlarnaLogo type={p.id === 'kp_one' ? 'pink' : 'black'} />
                  {t(`products-fe.products.${p.id}.name`)}
                </TitleWithKlarnaLogoContainer>
              </>
            }
          >
            <Typography textToken="text-style/text/paragraphs/body/bold">
              Meerkats Template ID:
            </Typography>
            <Typography textToken="text-style/text/paragraphs/body/medium">
              {p?.meerkatsTemplateId}
            </Typography>
            <br />
            <Typography textToken="text-style/text/paragraphs/body/bold">
              Terms and Conditions:
            </Typography>
            <List>
              {[
                ...(p?.termsAndConditions?.map((t, i: number) => (
                  <List.Item key={`${t}-${i}`}>
                    <Link href={t.href} target="_blank">
                      General terms:{' '}
                      {t.country === 'default' ? 'All languages/countries' : t.country}
                    </Link>
                  </List.Item>
                )) || []),
                <List.Item key="pricing-annex">
                  <Link href={currentConfig?.PRICING_ANNEX?.[p.id]} target="_blank">
                    Pricing Annex
                  </Link>
                </List.Item>,
              ]}
            </List>
          </ExpandableFrame>
        ))
      ) : (
        <Loader />
      )}
    </>
  );
};

ProductsComponent.propTypes = {
  ...componentPropTypes,
  ...enhancedComponentPropTypes,
};

export const Products = compose<EnhancedComponentProps, ComponentProps>(
  clientComponents.withPromiseBasedCapabilities({
    getProducts: CAPS.V2_PRODUCTS_GET,
  }),
  connect(state => ({
    supportedPlatforms: getSupportedPlatforms(state),
    t: getMerchantAwareTranslator(state),
  })),
)(ProductsComponent);
